import { useCallback, useEffect, useState } from "react";
import { useDispatch, useStore } from "react-redux";
import { Alert, Button, ModalBody, ModalFooter, Spinner, Table } from "react-bootstrap";
import { Modal as bsModal } from "bootstrap";
import { utils, writeFileXLSX } from "xlsx";

import "./AuftraegeTabelle.css";
import { fetchAuftrag } from "../reducers/auftrag-reducer";
import { ModalLayout } from "../popups/ModalLayout";
import AuftragDetails from "./AuftragDetails";
import { searchListe } from "./searchList";
import { Aktionen, EdelmetallBezeichnung, SortenBezeichnung } from "./components";
import { Navbar, Pagination, Tooltip } from "../utils/components";
import { Fold, InfoOutlinedIcon, PrintIcon, Unfold } from "../utils/components/icons";
import { AUFTRAG_STATUS, GESCHAEFTS_ART } from "../utils/enums";
import { formatNumber, formatDate, formatDateTime, formatIsoDate } from "../utils/functions";

const AuftraegeTabelle = (props) => {
  const store = useStore();
  const [loading, setLoading] = useState(false);
  const { configStorage, auftragStorage, metalsStorage, currenciesStorage } = store.getState();
  const [state, setState] = useState({ page: 1, rows: 10, fold: [], aktion: "", auftragsNummer: null });
  const liste = auftragStorage.getListe();
  const params = auftragStorage.getSearchParams();
  const { disabled, queryParams } = props || {};
  const dispatch = useDispatch();
  const apiCall = configStorage.getApiRequest("auftrag");

  // update Tablle nach dem Laden
  useEffect(() => {
    const unsub = store.subscribe(() => {
      const st = store.getState();
      if (!!st.auftragStorage.isLoading()) {
        setLoading(true);
      } else {
        if (loading) {
          setLoading(false);
          setState({ ...state, search: {}, page: 1 });
        }
      }
    });
    return unsub;
  });

  useEffect(() => {
    const treffer = liste.filter(item => searchListe(item, state.search));
    const length = treffer.length;
    if (length < state.rows && state.rows > 10) {
      setState({ ...state, rows: 10, page: 1 });
    }
    if ((state.page - 1) * state.rows > length && state.page > 1) {
      setState({ ...state, page: 1 });
    }
  }, [liste, state]);

  const mapGeschaeftsart = (geschaeftsart) => {
    return Object.keys(GESCHAEFTS_ART).filter(key => GESCHAEFTS_ART[key].KEY === geschaeftsart)
      .map(key => GESCHAEFTS_ART[key].BEZEICHNUNG)
      .join() || "Sonstige";
  };

  const isBestellung = (geschaeftsart) => {
    if (!geschaeftsart) {
      return true;
    }
    return geschaeftsart.endsWith('VK');
  };

  const mapAuftragsstatus = (auftragsStatus) => {
    return Object.keys(AUFTRAG_STATUS).filter(key => AUFTRAG_STATUS[key].KEY === Number(auftragsStatus))
      .map(key => AUFTRAG_STATUS[key].BEZEICHNUNG + " (" + auftragsStatus + ")")
      .join() || "Status " + auftragsStatus;
  };

  const exportFile = useCallback(() => {
    // const format = "0.000";
    const headers = [
      "Auftragsnummer",
      "Geschaeftsart",
      "Datum",
      "Auftragsstatus",
      "Kundenkonto",
      "Kennziffer",
      "Langbezeichnung",
      "Anzahl/Betrag",
      "Direktversand",
      "MandatsRef",
      "Erfasser",
      "Telefonnummer",
      "Beraterplatz",
      "Lieferadresse",
      "Gesamt-Betrag"
    ];
    const rows = liste.map((item) => (
      {
        Auftragsnummer: item.auftragsNummer,
        Geschaeftsart: mapGeschaeftsart(item.geschaeftsart),
        Datum: item.datum,
        Auftragsstatus: mapAuftragsstatus(item.auftragsStatus),
        Kundenkonto: item.iban,
        Kennziffer: item.kennziffer,
        Langbezeichnung: item.text,
        AnzahlBetrag: item.count,
        Direktversand: item.direktversandKz === "J" ? "ja" : "nein",
        MandatRef: item.mandatRef,
        Erfasser: item.bearbeiter.name,
        Telefonnummer: item.bearbeiter.telefon,
        Beraterplatz: item.bearbeiter.platz,
        Lieferadresse: isBestellung(item.geschaeftsart) ? item.lieferAdresse : "",
        Betrag: item.gesamtBetrag,
      }
    ));
    const worksheet = utils.json_to_sheet(rows);
    utils.sheet_add_aoa(worksheet, [headers], { origin: "A1" });
    const max_width = rows.reduce((prev, item) => Math.max(prev, String(item.Langbezeichnung).length), 15);
    const max_ga = rows.reduce((prev, item) => Math.max(prev, String(item.Geschaeftsart).length), 10);
    const max_status = rows.reduce((prev, item) => Math.max(prev, String(item.Auftragsstatus).length), 10);
    const max_konto = rows.reduce((prev, item) => Math.max(prev, String(item.Kundenkonto).length), 10);
    const max_mandat = rows.reduce((prev, item) => Math.max(prev, String(item.MandatRef).length), 10);
    const max_erfasser = rows.reduce((prev, item) => Math.max(prev, String(item.Erfasser).length), 10);
    const max_telfon = rows.reduce((prev, item) => Math.max(prev, String(item.Telefonnummer).length), 15);
    const max_platz = rows.reduce((prev, item) => Math.max(prev, String(item.Beraterplatz).length), 10);
    const max_adresse = rows.reduce((prev, item) => Math.max(prev, String(item.Lieferadresse).length), 10);
    const max_betrag = rows.reduce((prev, item) => Math.max(prev, String(item.Betrag).length), 15);
    const now = new Date();
    worksheet["!cols"] = [
      { wch: 15 },
      { wch: max_ga },
      { wch: 10 },
      { wch: max_status },
      { wch: max_konto },
      { wch: 10 },
      { wch: max_width },
      { wch: 12 },
      { wch: 12 },
      { wch: max_mandat + 1 },
      { wch: max_erfasser },
      { wch: max_telfon },
      { wch: max_platz },
      { wch: max_adresse },
      { wch: max_betrag },
    ];
    rows.forEach((_item, idx) => { // formatiere Zellen
      let addr = utils.encode_cell({ r: idx + 1, c: 7 });
      if (worksheet[addr]) worksheet[addr].z = "0.000";
      addr = utils.encode_cell({ r: idx + 1, c: 14 });
      if (worksheet[addr]) worksheet[addr].z = "0.00";
      addr = utils.encode_cell({ r: idx + 1, c: 0 });
      if (worksheet[addr]) worksheet[addr].z = "0";
    });
    utils.sheet_add_aoa(worksheet, [["Zeitpunkt der Erstellung"]], { origin: utils.encode_cell({ r: rows.length + 2, c: 0 }) });
    utils.sheet_add_aoa(worksheet, [[formatDateTime(now)]], { origin: utils.encode_cell({ r: rows.length + 2, c: 3 }) });
    utils.sheet_add_aoa(worksheet, [["Verwendete Suchkriterien:"]], { origin: utils.encode_cell({ r: rows.length + 3, c: 0 }) });
    const kriterien = Object.keys(params).map((param, idx) => {
      if (param === "auftragsStatus") return [queryParams[param].label, "", mapAuftragsstatus(params[param])];
      if (param === "geschaeftsart") return [queryParams[param].label, "", mapGeschaeftsart(params[param])];
      if (param === "kennziffer") return [queryParams[param].label, "", [params[param], rows[0].Langbezeichnung].join("/")];
      switch (queryParams[param].type) {
        case "date":
          return [queryParams[param].label, "", formatDate(params[param])];
        default:
          return [queryParams[param].label, "", params[param]];
      }
    });
    utils.sheet_add_aoa(worksheet, kriterien, { origin: utils.encode_cell({ r: rows.length + 4, c: 1 }) });

    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Auftragsübersicht");
    writeFileXLSX(workbook, "Auftragsübersicht_" + formatIsoDate(now).replace(/:/g, "-") + ".xlsx");
  }, [liste, queryParams, params]);

  const showModal = () => {
    const modal = document.querySelector("#details");
    if (modal) {
      bsModal.getOrCreateInstance(modal).show();
    }
  };

  const hideModal = () => {
    const modal = document.querySelector("#details");
    if (modal) {
      bsModal.getOrCreateInstance(modal).hide();
    }
  };

  const getDetails = useCallback(async (e) => {
    const target = e && (e.currentTarget || e.target);
    if (target) {
      const auftragsNummer = Number(target.dataset.auftragsNummer);
      if (auftragsNummer) {
        setState({ ...state, aktion: "loading", auftragsNummer });
        await Promise.resolve(dispatch(fetchAuftrag({ apiCall, auftragsNummer })));
        setState({ ...state, aktion: "", auftragsNummer: null });
        showModal();
      } else {
        hideModal();
      }
    } else {
      hideModal();
    }
  }, [apiCall, dispatch, state]);

  const onChange = useCallback((changed) => {
    if (changed) {
      if (typeof changed.search === "string") {
        let items = (metalsStorage.all || []).reduce((prev, i) => {
          prev[Number(i.kennziffer)] = i.name;
          return prev;
        }, {});
        (currenciesStorage.getItems() || []).reduce((prev, i) => {
          prev[i.kennziffer] = i.iso + "|" + i.name;
          return prev;
        }, items);

        changed.search = { search: changed.search, items };
        if (liste.filter(item => searchListe(item, changed.search)).length < state.rows * (state.page - 1)) {
          changed.page = 1;
        }
      }
      if (changed.rows) {
        let rows = Number(changed.rows) || 10;
        // first element in the page
        let pos = (state.page - 1) * state.rows + 1;
        // new page to display first element
        changed.page = Math.ceil(pos / rows);
      }
      setState({ ...state, ...changed });
    }
  }, [state, liste, metalsStorage.all, currenciesStorage]);

  const onClick = useCallback((e) => {
    const target = e && (e.currentTarget || e.target);
    if (target) {
      const col = target.dataset.col;
      const fold = [...state.fold];
      fold[col] = !fold[col];
      setState({ ...state, fold });
    }
  }, [state]);

  const treffer = liste.filter(item => searchListe(item, state.search));
  const length = treffer.length;
  let start = 1 + (state.page - 1) * state.rows;
  let end = start + state.rows - 1;
  if (length <= state.rows) {
    start = length ? 1 : 0;
    end = length;
  } else {
    if (end > length) end = length;
  }

  if (loading) {
    return (
      <div>
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }
  if (!auftragStorage.hasData()) {
    return null;
  }

  const hasKonto = liste.reduce((prev, a) => { return prev || !!String(a.iban).substring(12).trim(); }, false);

  return <div className="fs-6">
    {length
      ? <>
        <p className="mb-0">
          Zum Öffnen von Auftragsdetails und Stornierungsoptionen klicken Sie bitte auf den "Info Button"
          <InfoOutlinedIcon /> links neben der Auftragsnummer.
        </p>
        <p className="mb-0">
          Für die Erstellung eines Zweitdrucks der Auftragsbestätigung können Sie direkt auf das "Drucker-Logo" <PrintIcon /> klicken.
        </p>
      </>
      : null
    }
    <Navbar
      brand="Aufträge"
      pagination={Pagination}
      searchListe={searchListe}
      list={liste}
      page={state.page}
      rows={state.rows}
      search={state.search || {}}
      disabled={disabled}
      onChange={onChange}
    >
      <div className="text-right">
        <Button onClick={exportFile} disabled={!liste.length || disabled}>Export XLSX</Button>
      </div>
    </Navbar>
    {!length
      ? <Alert variant="warning">Es konnten keine passenden Aufträge gefunden werden</Alert>
      : <>
        {auftragStorage.hasMoreData()
          ? <Alert variant="warning">
            Es werden nicht alle möglichen Ergebnisse angezeigt. Schränken Sie die Suche eventuell ein.
          </Alert>
          : null
        }
        <Table className="condensed" variant="hover" size="sm" responsive aria-description="Liste der Aufträge die über EdelmetallShop oder SortenShop generiert wurden">
          <caption>Aufträge: {start}-{end} von {liste.length}</caption>
          <thead>
            <tr>
              <th><span className="aria-visible">Aktion</span></th>
              <th>
                {!state.fold[1]
                  ? <span>
                    <Fold data-col="1" onClick={onClick}></Fold>
                    Auftrags&shy;nummer
                  </span>
                  : <Tooltip title="Auftragsnummer" icon={Unfold} data-col="1" onClick={onClick}></Tooltip>
                }
              </th>
              <th>Geschäfts&shy;art</th>
              <th>Datum</th>
              <th>Auftrags&shy;status</th>
              {hasKonto
                ? <th>
                  {!state.fold[5]
                    ? <span>
                      <Fold data-col="5" onClick={onClick}></Fold>
                      Kunden&shy;konto
                    </span>
                    : <Tooltip title="Kundenkonto" icon={Unfold} data-col="5" onClick={onClick} ></Tooltip>
                  }
                </th>
                : null
              }
              <th>Kennziffer</th>
              <th>
                {!state.fold[7]
                  ? <span>
                    <Fold data-col="7" onClick={onClick}></Fold>
                    Lang&shy;be&shy;zeich&shy;nung
                  </span>
                  : <Tooltip title="Langbezeichnung" icon={Unfold} data-col="7" onClick={onClick} ></Tooltip>
                }
              </th>
              <th>Anzahl/Betrag</th>
            </tr>
          </thead>
          <tbody>
            {treffer.slice(start - 1, start - 1 + state.rows).map(auftrag => {
              const isSorten = !!String(auftrag.geschaeftsart).match(/^so/);
              const isEdelmetall = !!String(auftrag.geschaeftsart).match(/^em/);
              let bezeichnung = auftrag.text;
              if (!bezeichnung && isEdelmetall) {
                bezeichnung = <EdelmetallBezeichnung kennziffer={auftrag.kennziffer} name={(auftrag.order || {}).name} />;
              }
              if (!bezeichnung && isSorten) {
                bezeichnung = <SortenBezeichnung kennziffer={auftrag.kennziffer} name={(auftrag.order || {}).name} />;
              }
              return <tr
                key={"row" + String(auftrag.auftragsNummer)}
              >
                <td className="text-start">
                  {state.aktion === "loading" && state.auftragsNummer === auftrag.auftragsNummer
                    ? <Spinner className="details" animation="border" role="status" variant="primary" />
                    : <Button
                      className="p-0"
                      data-auftrags-nummer={auftrag.auftragsNummer}
                      data-bs-target="#details"
                      onClick={getDetails}
                      title="Details"
                      aria-label="Details"
                      variant="default"
                    >
                      <InfoOutlinedIcon></InfoOutlinedIcon>
                    </Button>
                  }
                  <Aktionen auftrag={auftrag} toastStatus />
                </td>
                <td aria-label="Auftragsnummer:">
                  {!state.fold[1]
                    ? auftrag.auftragsNummer
                    : null
                  }
                </td>
                <td aria-label="Geschäftsart:">
                  {mapGeschaeftsart(auftrag.geschaeftsart)}
                </td>
                <td aria-label="Datum:">
                  {formatDate(auftrag.datum)}
                </td>
                <td aria-label="Auftragsstatus:">
                  {mapAuftragsstatus(auftrag.auftragsStatus)}
                </td>
                {hasKonto
                  ? <td aria-label="Kundenkonto:">
                    {!state.fold[5]
                      ? String(auftrag.iban).substring(12)
                      : null
                    }
                  </td>
                  : null
                }
                <td aria-label="Kennziffer:">
                  {String(auftrag.kennziffer)}
                </td>
                <td aria-label="Bezeichnung:">
                  {!state.fold[7]
                    ? bezeichnung
                    : null
                  }
                </td>
                <td aria-label="Anzahl/Betrag:">
                  {isSorten
                    ? formatNumber(auftrag.count, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                    : formatNumber(auftrag.count, {
                      minimumFractionDigits: 3,
                      maximumFractionDigits: 3,
                    })
                  }
                </td>
              </tr>;
            })}
          </tbody>
        </Table>
        <ModalLayout modalName="details" title={"Details zum Auftrag " + (auftragStorage.getAuftrag() ? auftragStorage.getAuftrag().auftragsNummer : "")} size="xl">
          <ModalBody>
            <AuftragDetails></AuftragDetails>
          </ModalBody>
          <ModalFooter>
            <Aktionen auftrag={auftragStorage.getAuftrag()} displayStorno />
          </ModalFooter>
        </ModalLayout>
      </>
    }
  </div>;
};
export default AuftraegeTabelle;
